import { Controller } from "@hotwired/stimulus"

// One list of tasks on the triage review sheet (triage_runs/_review_change).
//
// Each row's chevron opens the row in place (its peek). The peek is fetched
// from the server the first time it opens, through the person's own run
// (TriageRunsController#peek), and never before: the sheet itself loads
// nothing per row.
//
// A long list shows one page at a time and has a search over the titles.
// Paging and search only hide rows. Every row stays in the form, so a ticked
// box on a hidden row is still part of the confirm, and the headline and the
// button (triage_review_controller.js) still count it. Text is set as text,
// never HTML.
const QUERY_LIMIT = 100
export const NOT_AVAILABLE = "This task isn’t available."

const tasks = (count) => (count === 1 ? "1 task" : `${count} tasks`)
export const normalize = (text) => String(text || "").toLowerCase().replace(/\s+/g, " ").trim().slice(0, QUERY_LIMIT)

// What the footer says for a page of `shown` rows out of `matches` (of `total`).
export function status(shown, matches, total, query) {
  if (!query) return shown === total ? `All ${total} shown` : `${shown} of ${total} shown`
  if (matches === 0) return "No task matches"
  if (shown === matches) return `${tasks(matches)} ${matches === 1 ? "matches" : "match"}`
  return `${shown} of ${matches} matching tasks shown`
}

export default class extends Controller {
  static targets = ["item", "query", "status", "more"]
  static values = { page: Number }

  connect() {
    this.limit = this.pageValue
    if (this.pageValue) this.show()
  }

  // A row's chevron: open or close its peek, and fetch it the first time.
  peek(event) {
    const button = event.currentTarget
    const panel = button.closest("[data-triage-list-target~='item']")?.querySelector("[data-role='peek']")
    if (!panel) return

    const open = button.getAttribute("aria-expanded") !== "true"
    button.setAttribute("aria-expanded", String(open))
    panel.hidden = !open
    if (!open || panel.dataset.loaded) return

    panel.dataset.loaded = "true"
    const frame = document.createElement("turbo-frame")
    frame.id = button.dataset.frameId
    frame.textContent = "Loading…"
    // Every refusal is the same empty 404, which Turbo would leave unrendered
    // ("Loading…" for ever), and a page without this frame (a sign-in page
    // after the session ended) would be "Content missing". Say so plainly.
    const unavailable = (event) => {
      event.preventDefault()
      frame.textContent = NOT_AVAILABLE
    }
    frame.addEventListener("turbo:before-fetch-response", (event) => {
      if (event.detail?.fetchResponse && !event.detail.fetchResponse.succeeded) unavailable(event)
    })
    frame.addEventListener("turbo:frame-missing", unavailable)
    frame.setAttribute("src", button.dataset.peekUrl)
    panel.appendChild(frame)
  }

  filter() {
    this.limit = this.pageValue
    this.show()
  }

  // The review controller left some rows out for the chosen action
  // (is-out): count and page only the rest.
  rows() {
    if (this.pageValue) this.show()
    const count = this.element.querySelector("[data-role='more-count']")
    if (count) count.textContent = String(this.element.querySelectorAll(".triage-more .triage-item:not(.is-out)").length)
  }

  more() {
    this.limit += this.pageValue
    this.show()
  }

  // Return in the search box finds; it never submits the confirm.
  keep(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  show() {
    const query = this.hasQueryTarget ? normalize(this.queryTarget.value) : ""
    const items = this.itemTargets.filter((item) => !item.classList.contains("is-out"))
    const matches = query ? items.filter((item) => normalize(item.querySelector(".triage-row__title")?.textContent).includes(query)) : items
    const shown = new Set(matches.slice(0, this.limit))
    items.forEach((item) => { item.hidden = !shown.has(item) })

    if (this.hasStatusTarget) this.statusTarget.textContent = status(shown.size, matches.length, items.length, query)
    if (this.hasMoreTarget) {
      const left = matches.length - shown.size
      this.moreTarget.hidden = left <= 0
      if (left > 0) this.moreTarget.textContent = `Show ${Math.min(this.pageValue, left)} more`
    }
  }
}
